export type DashboardActivityCategory =
  | 'missing-owner'
  | 'missing-contact'
  | 'missing-website'
  | 'missing-location'
  | 'missing-notes'

export type DashboardActivityEntry = {
  id: string
  leadId: string
  company: string
  action: string
  category?: DashboardActivityCategory | null
  fieldsResolved: number
  timestamp: number
  undoToken?: string
}

export type DashboardActivitySummary = {
  fieldsResolved: number
  prospectsUpdated: number
  recent: DashboardActivityEntry[]
}

export const DASHBOARD_ACTIVITY_EVENT =
  'wolf-forge-dashboard-activity-updated'

const DASHBOARD_ACTIVITY_CHANNEL =
  'wolf-forge-dashboard-activity-channel'

const ACTIVITY_REFRESH_MS = 5_000

const VALID_CATEGORIES =
  new Set<DashboardActivityCategory>([
    'missing-owner',
    'missing-contact',
    'missing-website',
    'missing-location',
    'missing-notes',
  ])

let activityCache: DashboardActivityEntry[] = []

const pendingCreates =
  new Map<string, DashboardActivityEntry>()

const pendingDeletes =
  new Set<string>()

type ActivitySyncWindow = Window & {
  __wolfForgeSupabaseActivityInstalled?: boolean
  __wolfForgeSupabaseActivityChannel?: BroadcastChannel
  __wolfForgeSupabaseActivityTimer?: number
}

function announceDashboardActivityRefresh(
  source: string,
): void {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent(
      DASHBOARD_ACTIVITY_EVENT,
      {
        detail: {
          source,
          timestamp: Date.now(),
        },
      },
    ),
  )
}

function broadcastDashboardActivityChange(): void {
  if (typeof window === 'undefined') return

  const syncWindow =
    window as ActivitySyncWindow

  syncWindow
    .__wolfForgeSupabaseActivityChannel
    ?.postMessage({
      type: 'activity-updated',
      timestamp: Date.now(),
    })
}

function localDateKey(
  date = new Date(),
): string {
  const year = date.getFullYear()

  const month = String(
    date.getMonth() + 1,
  ).padStart(2, '0')

  const day = String(
    date.getDate(),
  ).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function isDashboardActivityEntry(
  entry: unknown,
): entry is DashboardActivityEntry {
  if (
    !entry ||
    typeof entry !== 'object'
  ) {
    return false
  }

  const candidate =
    entry as Record<string, unknown>

  const category =
    candidate.category

  const validCategory =
    category === undefined ||
    category === null ||
    (
      typeof category === 'string' &&
      VALID_CATEGORIES.has(
        category as DashboardActivityCategory,
      )
    )

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.leadId === 'string' &&
    typeof candidate.company === 'string' &&
    typeof candidate.action === 'string' &&
    typeof candidate.fieldsResolved === 'number' &&
    Number.isFinite(candidate.fieldsResolved) &&
    typeof candidate.timestamp === 'number' &&
    Number.isFinite(candidate.timestamp) &&
    validCategory &&
    (
      candidate.undoToken === undefined ||
      typeof candidate.undoToken === 'string'
    )
  )
}

function sortActivity(
  entries: DashboardActivityEntry[],
): DashboardActivityEntry[] {
  return [...entries].sort(
    (left, right) =>
      right.timestamp - left.timestamp,
  )
}

function activitySignature(
  entries: DashboardActivityEntry[],
): string {
  return entries
    .map(
      (entry) =>
        [
          entry.id,
          entry.timestamp,
          entry.action,
          entry.fieldsResolved,
        ].join(':'),
    )
    .join('|')
}

function replaceCache(
  entries: DashboardActivityEntry[],
  source: string,
): void {
  const next = sortActivity(entries)

  const changed =
    activitySignature(next) !==
    activitySignature(activityCache)

  activityCache = next

  if (changed) {
    announceDashboardActivityRefresh(source)
  }
}

function mergeRemoteActivity(
  remote: DashboardActivityEntry[],
): DashboardActivityEntry[] {
  const byId =
    new Map<string, DashboardActivityEntry>()

  for (const entry of remote) {
    if (!pendingDeletes.has(entry.id)) {
      byId.set(entry.id, entry)
    }
  }

  for (const entry of pendingCreates.values()) {
    byId.set(entry.id, entry)
  }

  return sortActivity(
    [...byId.values()],
  )
}

async function readResponsePayload(
  response: Response,
): Promise<Record<string, unknown>> {
  try {
    const payload =
      await response.json()

    if (
      payload &&
      typeof payload === 'object' &&
      !Array.isArray(payload)
    ) {
      return payload as Record<string, unknown>
    }
  } catch {
    // The caller handles the HTTP failure below.
  }

  return {}
}

export async function refreshDashboardActivity(
  source = 'server',
): Promise<void> {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const response = await fetch(
      '/api/activity',
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          accept: 'application/json',
        },
      },
    )

    /*
     * The module can load before login has completed.
     * A later focus, timer, or activity event retries.
     */
    if (response.status === 401) {
      return
    }

    const payload =
      await readResponsePayload(response)

    if (!response.ok) {
      throw new Error(
        typeof payload.message === 'string'
          ? payload.message
          : 'Activity history could not be loaded.',
      )
    }

    const raw =
      Array.isArray(payload.activities)
        ? payload.activities
        : []

    const remote =
      raw.filter(
        isDashboardActivityEntry,
      )

    replaceCache(
      mergeRemoteActivity(remote),
      source,
    )
  } catch (cause) {
    console.error(
      'Activity history refresh failed.',
      cause,
    )
  }
}

function createTemporaryId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return `pending-${crypto.randomUUID()}`
  }

  return `pending-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`
}

export function getAllDashboardActivity():
DashboardActivityEntry[] {
  return [...activityCache]
}

export function getTodayDashboardActivity():
DashboardActivityEntry[] {
  const today =
    localDateKey()

  return activityCache.filter(
    (entry) =>
      localDateKey(
        new Date(entry.timestamp),
      ) === today,
  )
}

export function getDashboardActivityCategory(
  entry: DashboardActivityEntry,
): DashboardActivityCategory | null {
  if (
    entry.category &&
    VALID_CATEGORIES.has(entry.category)
  ) {
    return entry.category
  }

  const action =
    entry.action.toLowerCase()

  if (action.includes('owner')) {
    return 'missing-owner'
  }

  if (
    action.includes('contact') ||
    action.includes('email') ||
    action.includes('phone')
  ) {
    return 'missing-contact'
  }

  if (action.includes('website')) {
    return 'missing-website'
  }

  if (
    action.includes('location') ||
    action.includes('city') ||
    action.includes('state')
  ) {
    return 'missing-location'
  }

  if (action.includes('note')) {
    return 'missing-notes'
  }

  return null
}

export function recordDashboardActivity(
  entry: {
    leadId: string
    company: string
    action: string
    fieldsResolved: number
    undoToken?: string
  },
): DashboardActivityEntry {
  const temporaryEntry:
  DashboardActivityEntry = {
    id: createTemporaryId(),
    leadId: entry.leadId,
    company: entry.company,
    action: entry.action,
    category: null,
    fieldsResolved: entry.fieldsResolved,
    timestamp: Date.now(),

    /*
     * Undo becomes available only after Supabase
     * confirms the permanent activity row.
     */
    undoToken: undefined,
  }

  temporaryEntry.category =
    getDashboardActivityCategory(
      temporaryEntry,
    )

  pendingCreates.set(
    temporaryEntry.id,
    temporaryEntry,
  )

  replaceCache(
    [
      temporaryEntry,
      ...activityCache,
    ],
    'optimistic-create',
  )

  void (async () => {
    try {
      const response = await fetch(
        '/api/activity',
        {
          method: 'POST',
          credentials: 'include',

          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
          },

          body: JSON.stringify({
            leadId: entry.leadId,
            company: entry.company,
            action: entry.action,
            category:
              temporaryEntry.category,
            fieldsResolved:
              entry.fieldsResolved,
            undoToken:
              entry.undoToken,
          }),
        },
      )

      const payload =
        await readResponsePayload(response)

      if (!response.ok) {
        throw new Error(
          typeof payload.message === 'string'
            ? payload.message
            : 'Activity history could not be saved.',
        )
      }

      const created =
        payload.activity

      if (
        !isDashboardActivityEntry(
          created,
        )
      ) {
        throw new Error(
          'The activity server returned an invalid record.',
        )
      }

      pendingCreates.delete(
        temporaryEntry.id,
      )

      const next =
        activityCache.filter(
          (current) =>
            current.id !== temporaryEntry.id,
        )

      replaceCache(
        [
          created,
          ...next,
        ],
        'server-create',
      )

      broadcastDashboardActivityChange()
    } catch (cause) {
      pendingCreates.delete(
        temporaryEntry.id,
      )

      replaceCache(
        activityCache.filter(
          (current) =>
            current.id !== temporaryEntry.id,
        ),
        'create-failed',
      )

      console.error(
        'Activity history save failed.',
        cause,
      )
    }
  })()

  return temporaryEntry
}

export function removeDashboardActivity(
  id: string,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const previous =
    activityCache.find(
      (entry) => entry.id === id,
    )

  if (!previous) {
    return
  }

  pendingDeletes.add(id)

  replaceCache(
    activityCache.filter(
      (entry) => entry.id !== id,
    ),
    'optimistic-delete',
  )

  void (async () => {
    try {
      const response = await fetch(
        `/api/activity?id=${encodeURIComponent(id)}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            accept: 'application/json',
          },
        },
      )

      const payload =
        await readResponsePayload(response)

      if (!response.ok) {
        throw new Error(
          typeof payload.message === 'string'
            ? payload.message
            : 'Activity history could not be removed.',
        )
      }

      pendingDeletes.delete(id)

      broadcastDashboardActivityChange()
    } catch (cause) {
      pendingDeletes.delete(id)

      replaceCache(
        [
          previous,
          ...activityCache,
        ],
        'delete-failed',
      )

      console.error(
        'Activity history delete failed.',
        cause,
      )

      void refreshDashboardActivity(
        'delete-recovery',
      )
    }
  })()
}

export function getTodayDashboardActivitySummary():
DashboardActivitySummary {
  const entries =
    getTodayDashboardActivity()

  return {
    fieldsResolved:
      entries.reduce(
        (total, entry) =>
          total + entry.fieldsResolved,
        0,
      ),

    prospectsUpdated:
      new Set(
        entries.map(
          (entry) => entry.leadId,
        ),
      ).size,

    recent:
      entries.slice(0, 5),
  }
}

function installDashboardActivitySync(): void {
  if (typeof window === 'undefined') {
    return
  }

  const syncWindow =
    window as ActivitySyncWindow

  if (
    syncWindow
      .__wolfForgeSupabaseActivityInstalled
  ) {
    return
  }

  syncWindow
    .__wolfForgeSupabaseActivityInstalled =
      true

  if (
    typeof BroadcastChannel !==
    'undefined'
  ) {
    const channel =
      new BroadcastChannel(
        DASHBOARD_ACTIVITY_CHANNEL,
      )

    syncWindow
      .__wolfForgeSupabaseActivityChannel =
        channel

    channel.addEventListener(
      'message',
      () => {
        void refreshDashboardActivity(
          'broadcast',
        )
      },
    )
  }

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        void refreshDashboardActivity(
          'visibility',
        )
      }
    },
  )

  window.addEventListener(
    'focus',
    () => {
      void refreshDashboardActivity(
        'focus',
      )
    },
  )

  syncWindow
    .__wolfForgeSupabaseActivityTimer =
      window.setInterval(
        () => {
          void refreshDashboardActivity(
            'poll',
          )
        },
        ACTIVITY_REFRESH_MS,
      )

  void refreshDashboardActivity(
    'initial',
  )
}

installDashboardActivitySync()